import { API_BASE_PATH } from '../apiConfig';

type StepId = 'agents' | 'skills' | 'codebase' | 'pricing';
type LoadState = 'idle' | 'loading' | 'ready' | 'error';
type LaunchState = 'idle' | 'submitting' | 'accepted' | 'error';
type PricingTier = 'free';
type ScanType = 'smart-contract' | 'web';
type CodebaseSourceMode = 'github' | 'upload';

interface AgentDiscoveryResponse {
  skills?: unknown;
  skillGroups?: unknown;
  webApiSkills?: unknown;
  aiAgents?: unknown;
  aiAgentModels?: unknown;
  agents?: unknown;
}

interface ScanAcceptedResponse {
  message?: string;
  runId?: string;
  status?: string;
  runDirectory?: string;
  uploadedCodebase?: {
    fileName?: string;
    sizeBytes?: number;
  };
  selectedBranch?: string;
  scopePath?: string;
  scopePaths?: string[];
  preconditionsPath?: string;
  resultEmail?: string;
  selectedSkills?: string[];
  selectedAiAgents?: string[];
  selectedAiAgentModels?: { aiAgent: string; model?: string }[];
  error?: string;
}

type AgentModelsByAgent = Record<string, string[]>;

interface PricingModelMultiplier {
  aiAgent: string;
  model: string;
  multiplier: number;
}

interface PricingModelScanCost extends PricingModelMultiplier {
  scanRunCount: number;
  weightedScanRunCount: number;
  estimatedTokenCostUsd: number;
}

interface PricingCodebaseMetrics {
  scopePath?: string;
  scopePaths?: string[];
  fileCount: number;
  countedFileCount: number;
  totalBytes: number;
  nonCommentLineCount: number;
}

interface PricingEstimateResponse {
  pricingType: 'smart-contract' | 'web-api';
  currency: 'USD';
  baseRateUsdPerNonCommentLine: number;
  baseCodeCostUsd: number;
  totalUsd: number;
  selectedSkillCount: number;
  selectedModelCount: number;
  scanRunCount: number;
  skillMultiplierTotal: number;
  modelMultiplierTotal: number;
  weightedModelScanRunTotal: number;
  codebase: PricingCodebaseMetrics;
  modelMultipliers: PricingModelMultiplier[];
  modelScanCosts: PricingModelScanCost[];
  freeScan: {
    available: boolean;
    totalUsd: number;
  };
  formula: {
    text: string;
    baseRateUsdPerNonCommentLine: number;
    nonCommentLineCount: number;
    weightedModelScanRunTotal: number;
  };
}

interface GitHubAppConfigResponse {
  configured: boolean;
  appSlug?: string;
  installUrl?: string;
  connectUrl?: string;
  missing?: string[];
}

interface GitHubSessionResponse {
  sessionId: string;
  login?: string;
}

interface GitHubInstallationAccount {
  login: string;
  type?: string;
  avatarUrl?: string;
}

interface GitHubInstallationSummary {
  id: number;
  account: GitHubInstallationAccount;
  targetType?: string;
  repositorySelection?: string;
  htmlUrl?: string;
  appSlug?: string;
}

interface GitHubRepositorySummary {
  id: number;
  owner: string;
  repo: string;
  fullName: string;
  private: boolean;
  htmlUrl?: string;
  defaultBranch?: string;
  permissions?: Record<string, boolean>;
}

interface GitHubInstallationsResponse {
  installations?: GitHubInstallationSummary[];
}

interface GitHubRepositoriesResponse {
  repositories?: GitHubRepositorySummary[];
}

interface GitHubRepositoryBranchSummary {
  name: string;
  default: boolean;
  protected?: boolean;
  commitSha?: string;
}

interface GitHubBranchesResponse {
  repository?: {
    fullName?: string;
    defaultBranch?: string;
  };
  branches?: GitHubRepositoryBranchSummary[];
}

interface GitHubRepositoryDirectorySummary {
  path: string;
  name: string;
  depth: number;
}

interface GitHubRepositoryTreeResponse {
  repository?: {
    fullName?: string;
    defaultBranch?: string;
    branch?: string;
  };
  directories?: GitHubRepositoryDirectorySummary[];
}

type GitHubRepositoriesByInstallation = Record<number, GitHubRepositorySummary[]>;

interface GitHubRepositoryChoice {
  installation: GitHubInstallationSummary;
  repository: GitHubRepositorySummary;
}

interface SelectedAiModel {
  agent: string;
  model: string;
}

interface SelectedAiAgentModels {
  agent: string;
  models: string[];
}

interface ScanWorkflowDraft {
  selectedScanType: ScanType | null;
  currentStep: StepId;
  selectedSkills: string[];
  selectedAiModels: string[];
  codebaseSourceMode: CodebaseSourceMode;
  uploadedCodebaseFileName: string;
  selectedGithubRepositoryId: number | null;
  selectedGithubBranch: string;
  scopePath: string;
  scopePaths: string[];
  questionnaire: {
    nonStandardTokens: 'yes' | 'no' | '';
    trustedRoles: string;
    regularFunctions: string;
    adminRestrictions: string;
    assumptionsFilePath: string;
    externalContractsTrusted: 'yes' | 'no' | '';
  };
  selectedPricingTier: PricingTier | null;
  discountCode: string;
}

interface SkillInfo {
  title: string;
  description: string;
  githubUrl: string;
}

const SCAN_DRAFT_STORAGE_KEY = 'scas-scan-draft';
const GITHUB_SESSION_STORAGE_KEY = 'scas-github-session-id';
const GITHUB_INSTALLATION_STORAGE_KEY = 'scas-github-installation-id';
const INITIAL_STEP: StepId = 'agents';

const STEPS: { id: StepId; label: string }[] = [
  { id: 'agents', label: 'AI Agents' },
  { id: 'skills', label: 'Skills' },
  { id: 'codebase', label: 'Codebase' },
  { id: 'pricing', label: 'Pricing' }
];

const SKILL_LABELS: Record<string, string> = {
  pashov: 'Pashov',
  'sc-auditor': 'SC Auditor',
  quillshield: 'QuillShield',
  cyfrin: 'Cyfrin',
  'trail-of-bits': 'Trail of Bits',
  'trail-of-bits-supply-chain': 'Trail of bits',
  'trail-of-bits-static-analysis': 'Trail of bits',
  'semgrep-code-security': 'Semgrep',
  'nemesis-auditor': 'Nemesis Auditor',
  forefy: 'Forefy'
};

const SKILL_INFO: Record<string, SkillInfo> = {
  pashov: {
    title: 'Pashov',
    description: 'Professional smart contract security auditing skills and best practices from experienced auditor Pashov.',
    githubUrl: 'https://github.com/pashov/skills'
  },
  'sc-auditor': {
    title: 'SC Auditor',
    description: 'Comprehensive smart contract auditing toolkit with vulnerability patterns and security checks.',
    githubUrl: 'https://github.com/Archethect/sc-auditor'
  },
  quillshield: {
    title: 'QuillShield',
    description: 'Advanced security analysis skills from QuillAI Network for smart contract vulnerability detection.',
    githubUrl: 'https://github.com/quillai-network/quillshield_skills'
  },
  cyfrin: {
    title: 'Cyfrin',
    description: 'Solskill by Cyfrin - Modern smart contract security scanning and auditing methodologies.',
    githubUrl: 'https://github.com/Cyfrin/solskill'
  },
  'trail-of-bits': {
    title: 'Trail of Bits',
    description: 'Industry-leading security research skills from Trail of Bits security experts.',
    githubUrl: 'https://github.com/trailofbits/skills'
  },
  'nemesis-auditor': {
    title: 'Nemesis Auditor',
    description: 'Iterative deep-logic auditing that alternates first-principles Feynman review with state inconsistency mapping.',
    githubUrl: 'https://github.com/0xiehnnkta/nemesis-auditor'
  },
  forefy: {
    title: 'Forefy',
    description: 'Specialized smart contract audit context and security assessment methodologies.',
    githubUrl: 'https://github.com/forefy/.context/blob/main/skills/smart-contract-audit/SKILL.md'
  }
};

const AI_AGENT_LABELS: Record<string, string> = {
  codex: 'Codex',
  claude: 'Claude'
};

const FALLBACK_AGENT_MODELS: AgentModelsByAgent = {
  codex: ['gpt-5.6-sol', 'gpt-5.6-terra', 'gpt-5.5', 'gpt-5.4'],
  claude: ['claude-fable-5', 'claude-opus-4-8', 'claude-opus-4-7', 'claude-sonnet-4-6', 'claude-haiku-4-5']
};

const PRICING_TIER_LABELS: Record<PricingTier, string> = {
  free: 'Free'
};

const isStepId = (value: unknown): value is StepId => {
  return typeof value === 'string' && STEPS.some((step) => step.id === value);
};

const isPricingTier = (value: unknown): value is PricingTier => {
  return value === 'free';
};

const isScanType = (value: unknown): value is ScanType => {
  return value === 'smart-contract' || value === 'web';
};

const isCodebaseSourceMode = (value: unknown): value is CodebaseSourceMode => {
  return value === 'github' || value === 'upload';
};

const normalizeList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(new Set(
    value
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean)
  ));
};

const compactScopePaths = (scopePaths: string[]): string[] => {
  const uniqueScopes = Array.from(new Set(scopePaths
    .map((scopePath) => scopePath.trim().replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, ''))
    .filter(Boolean)
  )).sort((left, right) => left.localeCompare(right));

  return uniqueScopes.filter((scopePath, index) => {
    return !uniqueScopes.some((candidate, candidateIndex) => {
      return candidateIndex !== index && scopePath.startsWith(`${candidate}/`);
    });
  });
};

const hasAncestorScopePath = (scopePaths: string[], scopePath: string): boolean => {
  return scopePaths.some((candidate) => scopePath.startsWith(`${candidate}/`));
};

const isScopePathIncluded = (scopePaths: string[], scopePath: string): boolean => {
  return scopePaths.includes(scopePath) || hasAncestorScopePath(scopePaths, scopePath);
};

const formatScopePaths = (scopePaths: string[]): string => {
  if (scopePaths.length === 0) {
    return 'Repository root';
  }

  if (scopePaths.length <= 2) {
    return scopePaths.join(', ');
  }

  return `${scopePaths.length} folders`;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const normalizeAgentModels = (value: unknown, availableAgents: string[]): AgentModelsByAgent => {
  const modelsByAgent: AgentModelsByAgent = {};

  if (isRecord(value)) {
    for (const agent of availableAgents) {
      const models = normalizeList(value[agent]);

      if (models.length > 0) {
        modelsByAgent[agent] = models;
      }
    }
  }

  return modelsByAgent;
};

const normalizeAgentInfoModels = (value: unknown): AgentModelsByAgent => {
  if (!Array.isArray(value)) {
    return {};
  }

  return value.reduce<AgentModelsByAgent>((modelsByAgent, item) => {
    if (!isRecord(item)) {
      return modelsByAgent;
    }

    const name = typeof item.name === 'string' ? item.name.trim() : '';
    const models = normalizeList(item.models);

    if (name && models.length > 0) {
      modelsByAgent[name] = models;
    }

    return modelsByAgent;
  }, {});
};

const getDiscoveryAgentModels = (discovery: AgentDiscoveryResponse, availableAgents: string[]): AgentModelsByAgent => {
  const fromMap = normalizeAgentModels(discovery.aiAgentModels, availableAgents);
  const fromAgentInfo = normalizeAgentInfoModels(discovery.agents);
  const modelsByAgent: AgentModelsByAgent = {};

  for (const agent of availableAgents) {
    const models = fromMap[agent] ?? fromAgentInfo[agent] ?? FALLBACK_AGENT_MODELS[agent] ?? [];

    modelsByAgent[agent] = models;
  }

  return modelsByAgent;
};

const getAiModelKey = (agent: string, model: string): string => `${agent}\n${model}`;

const getOrderedSelectedAiModels = (
  availableAiAgents: string[],
  agentModelsByAgent: AgentModelsByAgent,
  selectedAiModels: Set<string>
): SelectedAiModel[] => {
  return availableAiAgents.flatMap<SelectedAiModel>((agent) => {
    const models = agentModelsByAgent[agent] ?? [];

    return models
      .filter((model) => selectedAiModels.has(getAiModelKey(agent, model)))
      .map((model) => ({ agent, model }));
  });
};

const getOrderedSelectedAiAgents = (orderedSelectedAiModels: SelectedAiModel[]): string[] => {
  return Array.from(new Set(orderedSelectedAiModels.map(({ agent }) => agent)));
};

const getSelectedAiAgentsWithModels = (orderedSelectedAiModels: SelectedAiModel[]): SelectedAiAgentModels[] => {
  const modelsByAgent = orderedSelectedAiModels.reduce<Record<string, string[]>>((grouped, selection) => {
    grouped[selection.agent] = [...(grouped[selection.agent] ?? []), selection.model];

    return grouped;
  }, {});

  return getOrderedSelectedAiAgents(orderedSelectedAiModels).map((agent) => ({
    agent,
    models: modelsByAgent[agent] ?? []
  }));
};

const getNextWorkflowStep = (currentStep: StepId): StepId => {
  const currentIndex = STEPS.findIndex((step) => step.id === currentStep);
  const nextStep = STEPS[currentIndex + 1];

  return nextStep?.id ?? currentStep;
};

const getPreviousWorkflowStep = (currentStep: StepId): StepId => {
  const currentIndex = STEPS.findIndex((step) => step.id === currentStep);
  const previousStep = STEPS[currentIndex - 1];

  return previousStep?.id ?? currentStep;
};

const formatModelLabel = (model: string): string => {
  const trimmed = model.trim();
  const normalized = trimmed.toLowerCase();

  if (normalized.startsWith('gpt-')) {
    const [version = '', ...variantParts] = trimmed.slice(4).split('-').filter(Boolean);
    const variant = variantParts
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

    return variant ? `GPT-${version} ${variant}` : `GPT-${version}`;
  }

  if (normalized.startsWith('claude-')) {
    return normalized
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  return trimmed;
};

const formatLabel = (value: string, labels: Record<string, string>): string => {
  if (labels[value]) {
    return labels[value];
  }

  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getErrorMessage = (payload: unknown, fallbackStatus: number): string => {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const { error } = payload as { error?: unknown };

    if (typeof error === 'string' && error.trim()) {
      return error;
    }
  }

  return `Request failed with status ${fallbackStatus}`;
};

const requestJson = async <ResponseType,>(path: string, init?: RequestInit): Promise<ResponseType> => {
  const headers = new Headers(init?.headers);

  if (typeof init?.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE_PATH}${path}`, {
    ...init,
    headers
  });
  const payload = await response.json().catch(() => undefined) as unknown;

  if (!response.ok) {
    throw new Error(getErrorMessage(payload, response.status));
  }

  return payload as ResponseType;
};

const getRouteRunId = (pathname = window.location.pathname): string | null => {
  const match = pathname.match(/^\/run\/([^/]+)\/?$/);

  if (!match) {
    return null;
  }

  return decodeURIComponent(match[1]);
};

const normalizeStoredYesNo = (value: unknown): 'yes' | 'no' | '' => {
  return value === 'yes' || value === 'no' ? value : '';
};

const normalizeStoredText = (value: unknown): string => {
  return typeof value === 'string' ? value : '';
};

const normalizeStoredScopePaths = (value: unknown, fallbackScopePath: string): string[] => {
  const scopePaths = normalizeList(value);

  return scopePaths.length > 0
    ? scopePaths
    : fallbackScopePath ? [fallbackScopePath] : [];
};

const normalizeStoredPositiveInteger = (value: unknown): number | null => {
  const number = typeof value === 'number' ? value : Number(value);

  return Number.isSafeInteger(number) && number > 0 ? number : null;
};

const getStoredScanDraft = (): ScanWorkflowDraft | null => {
  const rawValue = window.localStorage.getItem(SCAN_DRAFT_STORAGE_KEY);

  if (!rawValue) {
    return null;
  }

  try {
    const value = JSON.parse(rawValue) as unknown;

    if (!isRecord(value)) {
      window.localStorage.removeItem(SCAN_DRAFT_STORAGE_KEY);
      return null;
    }

    const questionnaire = isRecord(value.questionnaire) ? value.questionnaire : {};

    const scopePath = normalizeStoredText(value.scopePath);

    return {
      selectedScanType: isScanType(value.selectedScanType) ? value.selectedScanType : null,
      currentStep: isStepId(value.currentStep) ? value.currentStep : INITIAL_STEP,
      selectedSkills: normalizeList(value.selectedSkills),
      selectedAiModels: normalizeList(value.selectedAiModels),
      codebaseSourceMode: isCodebaseSourceMode(value.codebaseSourceMode) ? value.codebaseSourceMode : 'github',
      uploadedCodebaseFileName: normalizeStoredText(value.uploadedCodebaseFileName),
      selectedGithubRepositoryId: normalizeStoredPositiveInteger(value.selectedGithubRepositoryId),
      selectedGithubBranch: normalizeStoredText(value.selectedGithubBranch),
      scopePath,
      scopePaths: normalizeStoredScopePaths(value.scopePaths, scopePath),
      questionnaire: {
        nonStandardTokens: normalizeStoredYesNo(questionnaire.nonStandardTokens),
        trustedRoles: normalizeStoredText(questionnaire.trustedRoles),
        regularFunctions: normalizeStoredText(questionnaire.regularFunctions),
        adminRestrictions: normalizeStoredText(questionnaire.adminRestrictions),
        assumptionsFilePath: normalizeStoredText(questionnaire.assumptionsFilePath),
        externalContractsTrusted: normalizeStoredYesNo(questionnaire.externalContractsTrusted)
      },
      selectedPricingTier: isPricingTier(value.selectedPricingTier) ? value.selectedPricingTier : null,
      discountCode: normalizeStoredText(value.discountCode)
    };
  } catch {
    window.localStorage.removeItem(SCAN_DRAFT_STORAGE_KEY);
    return null;
  }
};

const storeScanDraft = (draft: ScanWorkflowDraft): void => {
  window.localStorage.setItem(SCAN_DRAFT_STORAGE_KEY, JSON.stringify(draft));
};

const clearScanDraft = (): void => {
  window.localStorage.removeItem(SCAN_DRAFT_STORAGE_KEY);
};

const getStoredGitHubSessionId = (): string => {
  return window.localStorage.getItem(GITHUB_SESSION_STORAGE_KEY) ?? '';
};

const storeGitHubSessionId = (sessionId: string): void => {
  if (sessionId) {
    window.localStorage.setItem(GITHUB_SESSION_STORAGE_KEY, sessionId);
  } else {
    window.localStorage.removeItem(GITHUB_SESSION_STORAGE_KEY);
  }
};

const getInitialGitHubSessionId = (): string => {
  const params = new URLSearchParams(window.location.search);

  return params.get('github_session') ?? getStoredGitHubSessionId();
};

const getGitHubCallbackError = (): string => {
  return new URLSearchParams(window.location.search).get('github_error') ?? '';
};

const getGitHubSetupInstallationId = (): number | null => {
  const params = new URLSearchParams(window.location.search);
  const installationId = Number(params.get('installation_id') ?? window.localStorage.getItem(GITHUB_INSTALLATION_STORAGE_KEY));

  return Number.isSafeInteger(installationId) && installationId > 0 ? installationId : null;
};

const storeGitHubInstallationId = (installationId: number | null): void => {
  if (installationId) {
    window.localStorage.setItem(GITHUB_INSTALLATION_STORAGE_KEY, String(installationId));
  } else {
    window.localStorage.removeItem(GITHUB_INSTALLATION_STORAGE_KEY);
  }
};

const clearGitHubCallbackParams = (): void => {
  const params = new URLSearchParams(window.location.search);
  const callbackParams = [
    'github_session',
    'github_connected',
    'github_error',
    'installation_id',
    'setup_action',
    'state'
  ];
  const hasCallbackParam = callbackParams.some((param) => params.has(param));

  if (!hasCallbackParam) {
    return;
  }

  for (const param of callbackParams) {
    params.delete(param);
  }

  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;

  window.history.replaceState({}, document.title, nextUrl);
};

const isPricingTierAvailable = (pricingTier: PricingTier | null): boolean => {
  if (!pricingTier) {
    return false;
  }

  return isPricingTier(pricingTier);
};

const getPricingTierLabel = (pricingTier: PricingTier | null): string => {
  return pricingTier ? PRICING_TIER_LABELS[pricingTier] : '';
};

export {
  AI_AGENT_LABELS,
  INITIAL_STEP,
  SKILL_INFO,
  SKILL_LABELS,
  STEPS,
  clearGitHubCallbackParams,
  clearScanDraft,
  compactScopePaths,
  formatLabel,
  formatModelLabel,
  formatScopePaths,
  getAiModelKey,
  getDiscoveryAgentModels,
  getGitHubCallbackError,
  getGitHubSetupInstallationId,
  getInitialGitHubSessionId,
  getNextWorkflowStep,
  getOrderedSelectedAiAgents,
  getOrderedSelectedAiModels,
  getPreviousWorkflowStep,
  getPricingTierLabel,
  getRouteRunId,
  getSelectedAiAgentsWithModels,
  getStoredScanDraft,
  isPricingTierAvailable,
  isRecord,
  isCodebaseSourceMode,
  isScopePathIncluded,
  isStepId,
  normalizeList,
  normalizeStoredText,
  requestJson,
  storeGitHubInstallationId,
  storeGitHubSessionId,
  storeScanDraft
};

export type {
  AgentDiscoveryResponse,
  AgentModelsByAgent,
  CodebaseSourceMode,
  GitHubAppConfigResponse,
  GitHubBranchesResponse,
  GitHubInstallationSummary,
  GitHubInstallationsResponse,
  GitHubRepositoriesByInstallation,
  GitHubRepositoriesResponse,
  GitHubRepositoryBranchSummary,
  GitHubRepositoryChoice,
  GitHubRepositoryDirectorySummary,
  GitHubRepositorySummary,
  GitHubRepositoryTreeResponse,
  GitHubSessionResponse,
  LaunchState,
  LoadState,
  PricingEstimateResponse,
  PricingModelScanCost,
  PricingTier,
  ScanAcceptedResponse,
  ScanType,
  ScanWorkflowDraft,
  SelectedAiAgentModels,
  SelectedAiModel,
  SkillInfo,
  StepId
};
